import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';

import { MatchingGrade } from '@app/settings/types';
import { matchingGradeLabelKey } from '@app/settings/utils';
import { Label } from '@app/ui/label';
import { Modal } from '@app/ui/modal';

import { GRADE_LABEL_SX } from '../constants';
import { JustificationView } from '../utils/build-justification-view';
import { formatCalendarDate } from '../utils/format-calendar-date';

interface ScoreJustificationModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly view: JustificationView;
}

// 15px has no typography token (`body1` 14, `subtitle1` 16) — matches `PairRow.tsx`'s own one-off.
const SECTION_HEADING_SX = { fontSize: 15, fontWeight: 600, lineHeight: 1.4 };

const RULE_ID_SX = { fontFamily: 'monospace', fontSize: 13, color: 'text.secondary' };

const SKIPPED_PREVIEW_COUNT = 6;

const MoreToggle = ({ expanded, onClick }: { expanded: boolean; onClick: () => void }) => {
  const { t } = useTranslation('common');

  return (
    <Box
      component="button"
      type="button"
      aria-expanded={expanded}
      onClick={onClick}
      sx={{
        p: 0,
        border: 'none',
        bgcolor: 'transparent',
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        font: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.4,
        color: 'primary.main',
        cursor: 'pointer',
        '&:hover': { color: 'primary.dark' },
      }}
    >
      {t(expanded ? 'matching.justification.viewLess' : 'matching.justification.viewMore')}
      {expanded ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
    </Box>
  );
};

// Rendered inside the Dialog so its expand state resets each time the modal closes (the Dialog
// unmounts its children; the modal itself stays mounted in `PairRow`).
const JustificationContent = ({ onClose, view }: Omit<ScoreJustificationModalProps, 'open'>) => {
  const { t } = useTranslation('common');
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [summaryOverflows, setSummaryOverflows] = useState(false);
  const [skippedExpanded, setSkippedExpanded] = useState(false);

  // Measured once on mount, while clamped.
  const summaryRef = useCallback((el: HTMLElement | null) => {
    if (el) {
      setSummaryOverflows(el.scrollHeight > el.clientHeight);
    }
  }, []);

  const skipped = skippedExpanded ? view.skipped : view.skipped.slice(0, SKIPPED_PREVIEW_COUNT);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {view.label && (
          <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
            {view.label}
          </Typography>
        )}

        {view.sides.map(side => (
          <Typography key={side.role} variant="body1" sx={{ lineHeight: 1.5 }}>
            {`${t(`matching.justification.roles.${side.role}`)}: ${side.name}`}
            {side.date ? `, ${formatCalendarDate(side.date)}` : ''}
          </Typography>
        ))}

        {view.score != null && (
          <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
            {t('matching.justification.scoreLine', { score: view.score })}
          </Typography>
        )}

        {view.summary && (
          <Typography
            ref={summaryRef}
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.5,
              textWrap: 'pretty',
              ...(!summaryExpanded && {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 4,
                overflow: 'hidden',
              }),
            }}
          >
            {view.summary}
          </Typography>
        )}

        {view.summary && (summaryExpanded || summaryOverflows) && (
          <Box sx={{ mt: -0.5, display: 'flex' }}>
            <MoreToggle expanded={summaryExpanded} onClick={() => setSummaryExpanded(value => !value)} />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={SECTION_HEADING_SX}>{t('matching.justification.sections.breakdown')}</Typography>

        {view.rows.length ? (
          // One grid for all rows so the badge column is as wide as the widest badge, not a fixed 96px —
          // otherwise a short badge sits much closer to its name than to the text after it.
          <Box sx={{ display: 'grid', gridTemplateColumns: '260px max-content minmax(0, 1fr)' }}>
            {view.rows.map(row => {
              const share = t('matching.justification.share', { pct: row.sharePct, count: row.points });

              return (
                <Box
                  key={row.ruleId}
                  sx={{
                    display: 'contents',
                    '& > *': { display: 'flex', alignItems: 'center', py: 1.25 },
                    '& > :not(:last-child)': { pr: 3 },
                    '&:not(:last-of-type) > *': { borderBottom: 1, borderColor: 'grey.400' },
                  }}
                >
                  <Typography variant="body1" sx={{ lineHeight: 1.4 }}>
                    {/* One inline span: the cell is a flex box, which would swallow the space before the id. */}
                    <span>
                      {row.name}{' '}
                      <Box component="span" sx={RULE_ID_SX}>
                        ({row.ruleId})
                      </Box>
                    </span>
                  </Typography>

                  <Box>
                    <Label
                      text={t(matchingGradeLabelKey(row.grade), row.grade)}
                      sx={{
                        height: 26,
                        px: 1.25,
                        fontSize: 13,
                        lineHeight: 1.4,
                        ...(GRADE_LABEL_SX[row.grade as MatchingGrade] ?? GRADE_LABEL_SX[MatchingGrade.clear]),
                      }}
                    />
                  </Box>

                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {row.counted ? `${t(`matching.justification.counted.${row.counted}`)} (${share})` : share}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary">
            {t('matching.justification.noBreakdown')}
          </Typography>
        )}
      </Box>

      {/* Design pulls this section 12px closer than the 24px section gap. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: -1.5 }}>
        <Typography sx={SECTION_HEADING_SX}>{t('matching.justification.sections.skipped')}</Typography>

        {skipped.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {skipped.map(skip => (
              <Typography key={skip.ruleId} variant="body1" sx={{ lineHeight: 1.5, textWrap: 'pretty' }}>
                {skip.name}{' '}
                <Box component="span" sx={RULE_ID_SX}>
                  ({skip.ruleId})
                </Box>
                {' — '}
                {skip.reason
                  ? t(`rules.matching.skipReason.${skip.reason}`)
                  : t('matching.justification.unknownReason')}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary">
            {t('matching.justification.noSkipped')}
          </Typography>
        )}

        {view.skipped.length > SKIPPED_PREVIEW_COUNT && (
          <MoreToggle expanded={skippedExpanded} onClick={() => setSkippedExpanded(value => !value)} />
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
        <Button variant="contained" color="primary" onClick={() => onClose()}>
          {t('matching.justification.close')}
        </Button>
      </Box>
    </Box>
  );
};

export const ScoreJustificationModal = ({ open, onClose, view }: ScoreJustificationModalProps) => {
  const { t } = useTranslation('common');

  return (
    <Modal open={open} onClose={() => onClose()} title={t('matching.justification.title')} maxWidth="lg">
      <JustificationContent onClose={onClose} view={view} />
    </Modal>
  );
};
